import { HTMLChakraProps, Box, Flex, Text, Button, Icon, BoxProps } from '@chakra-ui/react';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCopyToClipboard } from 'react-use';

import EmailIcon from '@/assets/icons/ic-share-email-40.svg';
import FbIcon from '@/assets/icons/ic-share-fb-dark.svg';
import LdIcon from '@/assets/icons/ic-share-ld-dark.svg';
import LinkIcon from '@/assets/icons/ic-share-link-dark.svg';
import XIcon from '@/assets/icons/ic-share-x.svg';

import { useAnalytics } from '@/context/Analytics';
import { ShareType } from '@/enums/ShareType';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { useResource } from '@/lib/datx-jsonapi-react';
import { getNewsArticleById } from '@/queries/news-articles';
import { getMailtoUrl } from '@/utils/getMailToUrl';
import { splitIdAndSlug } from '@/utils/helperFunctions';
import { openInNewWindow } from '@/utils/openInNewWindow';

export const ArticleSocialShare: FC<BoxProps> = ({ ...rest }) => {
  const [showNotification, setShowNotification] = useState(null);
  const [clipboardState, copyToClipboard] = useCopyToClipboard();
  const { t } = useTranslation();
  const analytics = useAnalytics();

  const { id: articleSlug } = useQueryParams<{ id: string }>({
    id: StringParam,
  });

  const { id: articleId } = splitIdAndSlug(articleSlug);

  const { data: article } = useResource(getNewsArticleById(articleId));

  const onShareClick = (medium: ShareType) => {
    let url: string;

    analytics.track('article_share_medium_select', { share_medium: medium });

    switch (medium) {
      case ShareType.Facebook:
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURI(window.location.href)}`;
        break;
      case ShareType.Twitter:
        url = `https://twitter.com/intent/tweet?url=${window.location.href}&text=${article.title}&via=`;
        break;
      case ShareType.LinkedIn:
        url = `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURI(window.location.href)}&title=${
          article.title
        }`;
        break;
      case ShareType.Email:
        url = getMailtoUrl('', article.title, window.location.href);
        break;
      case ShareType.Copy:
        copyToClipboard(window.location.href);

        setShowNotification(clipboardState.error ? t('common.copyError') : t('common.copySuccess'));
        setTimeout(() => setShowNotification(null), 2000);
        return;
      default:
        break;
    }

    openInNewWindow(url);
  };

  return (
    <Box mb={[20, null, null, 40]} {...rest}>
      <Flex
        flexDirection={['column', null, null, 'row']}
        justifyContent="space-between"
        alignItems={['flex-start', null, null, 'center']}
        mx={[null, null, 'auto']}
      >
        <Text textAlign="left" fontFamily="heading" mb={['6', null, null, 0]}>
          {t('common.articleShareTitle')}
        </Text>
        <Box>
          <Button mr={[8, null, null, 10]} variant="unstyled" onClick={() => onShareClick(ShareType.Facebook)}>
            <Icon boxSize="40px" as={FbIcon} />
          </Button>
          <Button mr={[8, null, null, 10]} variant="unstyled" onClick={() => onShareClick(ShareType.Twitter)}>
            <Icon boxSize="40px" as={XIcon} />
          </Button>
          <Button mr={[8, null, null, 10]} variant="unstyled" onClick={() => onShareClick(ShareType.LinkedIn)}>
            <Icon boxSize="40px" as={LdIcon} />
          </Button>
          <Button mr={[8, null, null, 10]} variant="unstyled" onClick={() => onShareClick(ShareType.Email)}>
            <Icon boxSize="40px" as={EmailIcon} />
          </Button>
          <Button variant="unstyled" onClick={() => onShareClick(ShareType.Copy)}>
            <Icon boxSize="40px" as={LinkIcon} />
          </Button>
        </Box>
      </Flex>
      <Box minH={6} style={{ opacity: showNotification ? '1' : '0' }} aria-live="polite" aria-atomic="true">
        {showNotification}
      </Box>
    </Box>
  );
};
